import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { toISO, addDays, rangeDays, DOW_SHORT, MONTH_SHORT, formatFull } from "@/lib/dates";
import { WarningCircle, CheckCircle, TrendUp } from "@phosphor-icons/react";

export default function CoveragePage() {
  const [anchorDate, setAnchorDate] = useState(new Date(2026, 3, 1));
  const [span, setSpan] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const days = useMemo(() => rangeDays(anchorDate, span), [anchorDate, span]);
  const startISO = toISO(days[0]);
  const endISO = toISO(days[days.length - 1]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const c = await api.coverage(startISO, endISO);
        setData(c);
      } catch {
        toast.error("Failed to load coverage");
      } finally {
        setLoading(false);
      }
    })();
  }, [startISO, endISO]);

  const byDate = useMemo(() => {
    const m = {};
    (data?.by_date || []).forEach((d) => (m[d.date] = d));
    return m;
  }, [data]);

  const totalSlots = data?.total_slots || 0;
  const summary = useMemo(() => {
    if (!data) return { avgFill: 0, worstGap: 0, worstDate: null, fullDays: 0 };
    let sumFill = 0;
    let worstGap = 0;
    let worstDate = null;
    let fullDays = 0;
    days.forEach((d) => {
      const iso = toISO(d);
      const row = byDate[iso] || { filled: 0, gaps: totalSlots };
      sumFill += row.filled;
      if (row.gaps > worstGap) {
        worstGap = row.gaps;
        worstDate = iso;
      }
      if (row.gaps === 0) fullDays++;
    });
    return {
      avgFill: days.length ? Math.round((sumFill / (days.length * totalSlots)) * 100) : 0,
      worstGap,
      worstDate,
      fullDays,
    };
  }, [data, days, byDate, totalSlots]);

  return (
    <div className="p-6 min-h-screen">
      <header className="flex items-end justify-between border-b-2 border-black pb-4 mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight leading-none">
            COVERAGE INSIGHTS
          </h1>
          <div className="text-[10px] font-mono-plex tracking-widest text-zinc-500 mt-2">
            {startISO} → {endISO} · TOTAL SLOTS PER DAY {totalSlots}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-black" data-testid="coverage-span">
            {[7, 14, 30, 60].map((n) => (
              <button
                key={n}
                onClick={() => setSpan(n)}
                data-testid={`span-${n}`}
                className={`px-3 py-2 text-[10px] font-mono-plex tracking-widest font-semibold transition-colors ${
                  span === n ? "bg-black text-white" : "bg-white hover:bg-zinc-100"
                }`}
              >
                {n}D
              </button>
            ))}
          </div>
          <div className="flex border border-black">
            <button
              onClick={() => setAnchorDate((d) => addDays(d, -span))}
              className="px-3 h-9 hover:bg-zinc-100 border-r border-black text-xs font-mono-plex"
            >
              ←
            </button>
            <button
              onClick={() => setAnchorDate((d) => addDays(d, span))}
              className="px-3 h-9 hover:bg-zinc-100 text-xs font-mono-plex"
            >
              →
            </button>
          </div>
        </div>
      </header>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Kpi
          icon={<TrendUp size={20} weight="bold" />}
          label="AVG FILL RATE"
          value={`${summary.avgFill}%`}
          testid="kpi-avg-fill"
        />
        <Kpi
          icon={<CheckCircle size={20} weight="bold" />}
          label="FULLY-STAFFED DAYS"
          value={summary.fullDays}
          testid="kpi-full-days"
        />
        <Kpi
          icon={<WarningCircle size={20} weight="bold" />}
          label="WORST GAP"
          value={summary.worstGap}
          testid="kpi-worst-gap"
          accent="red"
        />
        <Kpi
          label="WORST DATE"
          value={summary.worstDate || "—"}
          testid="kpi-worst-date"
          mono
        />
      </div>

      {/* Daily bars */}
      <div className="border-2 border-black bg-white">
        <div className="px-4 py-3 border-b-2 border-black flex items-center justify-between">
          <div className="font-heading font-bold text-sm tracking-widest">DAILY COVERAGE</div>
          <div className="font-mono-plex text-[10px] text-zinc-500">
            {loading ? "LOADING…" : `${days.length} DAYS`}
          </div>
        </div>
        <div className="divide-y divide-zinc-200">
          {days.map((d) => {
            const iso = toISO(d);
            const row = byDate[iso] || { filled: 0, gaps: totalSlots };
            const pct = totalSlots > 0 ? (row.filled / totalSlots) * 100 : 0;
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            return (
              <div
                key={iso}
                className={`grid grid-cols-[160px_1fr_100px_80px] items-center px-4 py-2 ${
                  isWeekend ? "bg-zinc-50" : ""
                }`}
                data-testid={`coverage-row-${iso}`}
              >
                <div className="font-mono-plex text-xs">
                  <span className="text-zinc-500 tracking-widest mr-2">
                    {DOW_SHORT[d.getDay()]}
                  </span>
                  {d.getDate()} {MONTH_SHORT[d.getMonth()]}
                </div>
                <div className="h-3 bg-zinc-100 border border-zinc-300 relative">
                  <div
                    className={`h-full ${
                      pct === 100 ? "bg-emerald-600" : pct >= 75 ? "bg-black" : pct >= 40 ? "bg-yellow-400" : "bg-red-500"
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="font-mono-plex text-xs text-right pr-3">
                  {row.filled}/{totalSlots}
                </div>
                <div className="text-right">
                  {row.gaps > 0 ? (
                    <span className="font-mono-plex text-[10px] tracking-widest text-red-700 bg-red-50 border border-red-500 px-2 py-0.5">
                      -{row.gaps}
                    </span>
                  ) : (
                    <span className="font-mono-plex text-[10px] tracking-widest text-emerald-700">
                      FULL
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, testid, mono, accent }) {
  return (
    <div
      className={`border-2 border-black p-4 bg-white flex flex-col justify-between ${
        accent === "red" ? "" : ""
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between text-zinc-500">
        <div className="text-[10px] font-mono-plex tracking-widest">{label}</div>
        {icon}
      </div>
      <div
        className={`mt-4 font-heading font-black tracking-tight leading-none ${
          mono ? "text-lg font-mono-plex" : "text-4xl"
        } ${accent === "red" ? "text-red-700" : ""}`}
      >
        {value}
      </div>
    </div>
  );
}
